import { splitSearchTokens } from '@/shared/domain/searchTokens'
import type { ClientRepository } from '../../application/ClientRepository'
import type {
  ClientContractState,
  ClientDossierStatus,
  ClientListItem,
  ClientRecord,
  ClientStats,
} from '../../domain/client'
import type { ClientNote } from '../../domain/clientNote'
import type { ClientAdditionalContact } from '../../domain/clientAdditionalContact'
import { clientsFixture } from './clientsFixtures'

const fixtureDate = '2026-08-08T00:00:00.000Z'
const fixtureSeason = '2026–2027'

/** Les libellés de démonstration reprennent les états métier de la liste. */
function fixtureContractState(status: string): ClientContractState {
  if (status === 'Actif') return 'ACTIVE'
  if (status === 'À renouveler') return 'QUOTE_PENDING'
  if (status === 'Suspendu') return 'AWAITING_PAYMENT'
  return 'NONE'
}

/** Refonte Clients, Phase 3 : équivalent démonstration de `deriveClientDossierStatus`. */
function fixtureDossierStatus(status: string): ClientDossierStatus {
  if (status === 'Actif') return 'ACTIVE'
  if (status === 'À renouveler') return 'FOLLOW_UP'
  if (status === 'Suspendu') return 'SUSPENDED'
  return 'NONE'
}

export const fixtureClientRecords: ClientListItem[] = clientsFixture.map(
  ([name, type, address, status, contract, route, balance], index) => ({
    id: index === 0 ? 'fixture-client-000083' : `fixture-client-${String(index + 1).padStart(6, '0')}`,
    number: index === 0 ? 'CLI-000083' : `CLI-${String(index + 84).padStart(6, '0')}`,
    type: type === 'Commercial' ? 'COMMERCIAL' : 'RESIDENTIAL',
    status: status === 'Actif' ? 'ACTIVE' : 'INACTIVE',
    statusLabel: status,
    language: 'FR',
    displayName: name,
    phone: index === 0 ? '(514) 555–4821' : '(450) 555–0100',
    email: index === 0 ? 'diane.laviolette@email.com' : `client${String(index + 1)}@example.test`,
    ...(address
      ? {
          address: {
            line1: address,
            city: 'Saint-Jérôme',
            province: 'QC',
            postalCode: index === 0 ? 'J7Z 0C6' : 'J7Z 1A1',
          },
        }
      : {}),
    ...(contract ? { activeContract: contract } : {}),
    ...(route ? { route } : {}),
    balanceLabel: balance,
    contractState: fixtureContractState(status),
    ...(fixtureContractState(status) === 'ACTIVE' ? { contractSeason: fixtureSeason } : {}),
    dossierStatus: fixtureDossierStatus(status),
    activeContractCount: fixtureContractState(status) === 'ACTIVE' ? 1 : 0,
    dossierContracts:
      fixtureDossierStatus(status) !== 'NONE' && contract
        ? [{ id: `fixture-contract-${String(index + 1).padStart(6, '0')}`, number: contract }]
        : [],
    createdAt: fixtureDate,
    updatedAt: fixtureDate,
  }),
)

const detailFixture: ClientRecord = {
  id: 'fixture-client-000083',
  number: 'CLI-000083',
  type: 'RESIDENTIAL',
  status: 'ACTIVE',
  language: 'FR',
  displayName: 'Diane Laviolette',
  firstName: 'Diane',
  lastName: 'Laviolette',
  phone: '(514) 555–4821',
  email: 'diane.laviolette@email.com',
  address: {
    line1: '1450, Rue des Pins',
    city: 'Saint-Jérôme',
    province: 'QC',
    postalCode: 'J7Z 0C6',
  },
  createdAt: fixtureDate,
  updatedAt: fixtureDate,
}

const fixtureStats: ClientStats = {
  total: 1248,
  active: 1220,
  inactive: 28,
  residential: 987,
  commercial: 261,
  activeContracts: 1180,
}

const fixtureClientNotes: ClientNote[] = []
const fixtureAdditionalContacts = new Map<string, ClientAdditionalContact>()

/** Clients créés pendant la session, jamais persistés. */
const createdClients = new Map<
  string,
  {
    clientId: string
    displayName: string
    firstName?: string
    lastName?: string
    company?: string
    phone?: string
    mobilePhone?: string
    email?: string
  }
>()

/** Lecture destinée aux fixtures voisines : le Contrat a besoin du contact. */
export function fixtureCreatedClient(clientId: string) {
  return createdClients.get(clientId)
}

export function createFixtureClientRepository(
  source: 'fixtures' | 'fixture-fallback' = 'fixtures',
): ClientRepository {
  return {
    list(filters) {
      const search = filters.search?.trim().toLocaleLowerCase('fr-CA')
      // Même règle que le repository Supabase : chaque mot saisi doit
      // correspondre à au moins un champ, dans n'importe quel ordre.
      const tokens = search ? splitSearchTokens(search) : []
      const filtered = fixtureClientRecords.filter((client) => {
        const haystack = [
          client.number,
          client.displayName,
          client.phone,
          client.email,
          client.address?.line1,
          client.address?.city,
          client.activeContract,
        ].filter(Boolean)
        const matchesSearch =
          tokens.length === 0 ||
          tokens.every((token) => haystack.some((value) => value?.toLocaleLowerCase('fr-CA').includes(token)))
        return (
          matchesSearch &&
          (!filters.status || client.status === filters.status) &&
          (!filters.type || client.type === filters.type) &&
          (!filters.dossierStatus || client.dossierStatus === filters.dossierStatus) &&
          (!filters.clientIds || filters.clientIds.includes(client.id)) &&
          (!filters.vip || client.vip === true) &&
          (!filters.contractActivity ||
            (client.contractState === 'ACTIVE') === (filters.contractActivity === 'WITH_ACTIVE'))
        )
      })
      return Promise.resolve({
        items: filtered,
        page: filters.page,
        pageSize: filters.pageSize,
        total:
          search ||
          filters.status ||
          filters.type ||
          filters.dossierStatus ||
          filters.clientIds ||
          filters.vip ||
          filters.contractActivity
            ? filtered.length
            : fixtureStats.total,
        stats: fixtureStats,
        meta: { source, retrievedAt: new Date().toISOString(), anomalies: 0 },
      })
    },
    getByReference(reference) {
      if (!['CLI-000083', 'fixture-client-000083'].includes(reference)) return Promise.resolve(null)
      return Promise.resolve({
        client: detailFixture,
        meta: { source, retrievedAt: new Date().toISOString(), anomalies: 0 },
      })
    },
    /**
     * Enregistre en mémoire, le temps de la session du navigateur.
     *
     * Les fixtures refusent d'inventer une donnée contractuelle, et c'est la
     * bonne règle. Rejouer ce que l'opérateur vient de saisir n'invente rien :
     * c'est ce qui permet à l'Assistant Nouvelle soumission d'être parcouru en
     * entier sans Supabase, jusqu'au document produit.
     */
    create(command) {
      const clientId = crypto.randomUUID()
      const displayName =
        [command.firstName, command.lastName].filter(Boolean).join(' ').trim() ||
        (command.company ?? '').trim()
      createdClients.set(clientId, { ...command, clientId, displayName })
      return Promise.resolve({
        clientId,
        number: `CLI-9${String(createdClients.size).padStart(5, '0')}`,
        displayName,
      })
    },
    findDuplicates() {
      // Aucune suggestion n'est simulée : elles dépendent des Clients réels.
      return Promise.resolve([])
    },
    updateStatus(command) {
      detailFixture.status = command.status
      if (command.motif) detailFixture.statutMotif = command.motif
      return Promise.resolve()
    },
    update(command) {
      const updatedAt = new Date().toISOString()
      const created = createdClients.get(command.clientId)
      if (created) {
        const nextFirst = command.firstName ?? created.firstName
        const nextLast = command.lastName ?? created.lastName
        const nextCompany = command.companyName ?? created.company
        createdClients.set(command.clientId, {
          ...created,
          ...(command.firstName !== undefined ? { firstName: command.firstName } : {}),
          ...(command.lastName !== undefined ? { lastName: command.lastName } : {}),
          ...(command.companyName !== undefined ? { company: command.companyName } : {}),
          ...(command.phone !== undefined ? { phone: command.phone } : {}),
          ...(command.mobilePhone !== undefined ? { mobilePhone: command.mobilePhone } : {}),
          ...(command.email !== undefined ? { email: command.email } : {}),
          displayName:
            (nextCompany ?? '').trim() ||
            [nextFirst, nextLast].filter(Boolean).join(' ').trim() ||
            created.displayName,
        })
        return Promise.resolve({ clientId: command.clientId, updatedAt })
      }
      const matchesDetail = [detailFixture.id, detailFixture.number].includes(command.clientId)
      const listRecord = fixtureClientRecords.find((record) => record.id === command.clientId)
      if (!matchesDetail && !listRecord) return Promise.reject(new Error('CLIENT_NOT_FOUND'))
      const applyName = (
        target: Pick<ClientRecord, 'firstName' | 'lastName' | 'companyName' | 'displayName'>,
      ) => {
        if (
          command.firstName === undefined &&
          command.lastName === undefined &&
          command.companyName === undefined
        )
          return
        if (command.firstName !== undefined) {
          if (command.firstName) target.firstName = command.firstName
          else delete target.firstName
        }
        if (command.lastName !== undefined) {
          if (command.lastName) target.lastName = command.lastName
          else delete target.lastName
        }
        if (command.companyName !== undefined) {
          if (command.companyName) target.companyName = command.companyName
          else delete target.companyName
        }
        target.displayName =
          target.companyName ?? [target.firstName, target.lastName].filter(Boolean).join(' ').trim()
      }
      const applyAddress = (target: { address?: ClientRecord['address'] }) => {
        if (
          command.address === undefined &&
          command.addressLine2 === undefined &&
          command.city === undefined &&
          command.postalCode === undefined &&
          command.latitude === undefined &&
          command.longitude === undefined
        )
          return
        const line2 = command.addressLine2 ?? target.address?.line2 ?? ''
        target.address = {
          line1: command.address ?? target.address?.line1 ?? '',
          ...(line2.trim() ? { line2: line2.trim() } : {}),
          city: command.city ?? target.address?.city ?? '',
          province: command.province ?? target.address?.province ?? 'QC',
          postalCode: command.postalCode ?? target.address?.postalCode ?? '',
          ...(command.latitude !== undefined ? { latitude: command.latitude } : {}),
          ...(command.longitude !== undefined ? { longitude: command.longitude } : {}),
        }
      }
      if (matchesDetail) {
        if (command.phone !== undefined) detailFixture.phone = command.phone
        if (command.email !== undefined) detailFixture.email = command.email
        applyName(detailFixture)
        applyAddress(detailFixture)
        detailFixture.updatedAt = updatedAt
      }
      if (listRecord) {
        if (command.phone !== undefined) listRecord.phone = command.phone
        if (command.email !== undefined) listRecord.email = command.email
        applyName(listRecord)
        applyAddress(listRecord)
        listRecord.updatedAt = updatedAt
      }
      return Promise.resolve({ clientId: command.clientId, updatedAt })
    },
    listNotes(clientId) {
      return Promise.resolve(fixtureClientNotes.filter((note) => note.clientId === clientId))
    },
    createNote(command) {
      const note = {
        id: crypto.randomUUID(),
        clientId: command.clientId,
        message: command.message,
        noteType: command.noteType,
        isImportant: command.isImportant,
        isPinned: command.isPinned,
        dismissedFromOverview: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }
      fixtureClientNotes.unshift(note)
      return Promise.resolve(note)
    },
    updateNote(command) {
      const note = fixtureClientNotes.find((candidate) => candidate.id === command.id)
      if (!note) throw new Error('CLIENT_NOTE_NOT_FOUND')
      note.message = command.message
      note.noteType = command.noteType
      note.isImportant = command.isImportant
      note.isPinned = command.isPinned
      note.updatedAt = new Date().toISOString()
      return Promise.resolve(note)
    },
    softDeleteNote(noteId) {
      const index = fixtureClientNotes.findIndex((candidate) => candidate.id === noteId)
      if (index >= 0) fixtureClientNotes.splice(index, 1)
      return Promise.resolve()
    },
    setNotePinned(noteId, pinned) {
      const note = fixtureClientNotes.find((candidate) => candidate.id === noteId)
      if (note) note.isPinned = pinned
      return Promise.resolve()
    },
    dismissNoteFromOverview(noteId) {
      const note = fixtureClientNotes.find((candidate) => candidate.id === noteId)
      if (note) note.dismissedFromOverview = true
      return Promise.resolve()
    },
    getAdditionalContact(clientId) {
      return Promise.resolve(fixtureAdditionalContacts.get(clientId) ?? null)
    },
    upsertAdditionalContact(command) {
      const contact = {
        id: fixtureAdditionalContacts.get(command.clientId)?.id ?? crypto.randomUUID(),
        clientId: command.clientId,
        name: command.name,
        relation: command.relation,
        ...(command.phone ? { phone: command.phone } : {}),
        ...(command.mobilePhone ? { mobilePhone: command.mobilePhone } : {}),
        ...(command.email ? { email: command.email } : {}),
      }
      fixtureAdditionalContacts.set(command.clientId, contact)
      return Promise.resolve(contact)
    },
    deleteAdditionalContact(clientId) {
      fixtureAdditionalContacts.delete(clientId)
      return Promise.resolve()
    },
    delete() {
      return Promise.reject(new Error('CLIENT_DELETE_SUPABASE_REQUIRED'))
    },
  }
}
